export type ProductSlug = string

export type ProductUrlSlug = string

export interface ProductConfig {
  slug: ProductSlug
  urlSlug: ProductUrlSlug
  name: string
  singularName: string
  pluralName: string
  id: string
  identifierPrefix: string
  optionalMetadataFields?: string[]
  defaultTruckloadQuantity: number
}

export function isProductSlug(slug: string): slug is ProductSlug {
  return typeof slug === 'string' && slug in PRODUCT_CATALOG
}

export const PRODUCT_CATALOG: Readonly<Record<ProductSlug, ProductConfig>> = {
  pallet: {
    slug: 'pallet',
    urlSlug: 'pallets',
    name: 'Pallets',
    singularName: 'Pallet',
    pluralName: 'Pallets',
    id: '64e85afa-bde0-427c-a716-e9639c530728',
    identifierPrefix: 'PAL',
    defaultTruckloadQuantity: 616,
  },
  gaylord: {
    slug: 'gaylord',
    urlSlug: 'gaylord-boxes',
    name: 'Gaylord Boxes',
    singularName: 'Gaylord Box',
    pluralName: 'Gaylord Boxes',
    id: 'a9e3588f-b019-437a-9849-bf7476372a19',
    identifierPrefix: 'GLD',
    optionalMetadataFields: ['previouscontents'],
    defaultTruckloadQuantity: 550,
  },
  ibctote: {
    slug: 'ibctote',
    urlSlug: 'ibc-totes',
    name: 'IBC Totes',
    singularName: 'IBC Tote',
    pluralName: 'IBC Totes',
    id: '4d46e2d0-90d2-45a5-852c-de16ea5108d1',
    identifierPrefix: 'IBC',
    optionalMetadataFields: ['previous', 'capacity'],
    defaultTruckloadQuantity: 60,
  },
  metaldrum: {
    slug: 'metaldrum',
    urlSlug: 'metal-drums',
    name: 'Metal Drums',
    singularName: 'Metal Drum',
    pluralName: 'Metal Drums',
    id: '9e5ef2e2-b7bf-4157-a87a-a8a8e2a24959',
    identifierPrefix: 'MDR',
    optionalMetadataFields: ['capacity'],
    defaultTruckloadQuantity: 208,
  },
  plasticdrum: {
    slug: 'plasticdrum',
    urlSlug: 'plastic-drums',
    name: 'Plastic Drums',
    singularName: 'Plastic Drum',
    pluralName: 'Plastic Drums',
    id: '34121abb-047e-4556-b18a-714e96a3408c',
    identifierPrefix: 'PDR',
    optionalMetadataFields: ['capacity'],
    defaultTruckloadQuantity: 208,
  },
  fiberdrum: {
    slug: 'fiberdrum',
    urlSlug: 'fiber-drums',
    name: 'Fiber Drums',
    singularName: 'Fiber Drum',
    pluralName: 'Fiber Drums',
    id: 'c2b7e0a4-5d1f-4e8a-9b3c-7f6d2a1e8c40',
    identifierPrefix: 'FDR',
    optionalMetadataFields: ['capacity'],
    defaultTruckloadQuantity: 208,
  },
  woodcrate: {
    slug: 'woodcrate',
    urlSlug: 'wood-crates',
    name: 'Wood Crates',
    singularName: 'Wood Crate',
    pluralName: 'Wood Crates',
    id: '67183314-6a77-4e08-a596-2ec0c05eafa9',
    identifierPrefix: 'WCR',
    defaultTruckloadQuantity: 200,
  },
  plasticpallet: {
    slug: 'plasticpallet',
    urlSlug: 'plastic-pallets',
    name: 'Plastic Pallets',
    singularName: 'Plastic Pallet',
    pluralName: 'Plastic Pallets',
    id: 'f3696e81-9526-4fa1-be2f-4c1536d460a8',
    identifierPrefix: 'PPL',
    defaultTruckloadQuantity: 520,
  },
  woodenspool: {
    slug: 'woodenspool',
    urlSlug: 'wooden-spools',
    name: 'Wooden Spools',
    singularName: 'Wooden Spool',
    pluralName: 'Wooden Spools',
    id: '1a2a9686-c140-47d3-ab20-54ca5d4ca3a1',
    identifierPrefix: 'WSP',
    defaultTruckloadQuantity: 100,
  },
  plasticcrate: {
    slug: 'plasticcrate',
    urlSlug: 'plastic-crates',
    name: 'Plastic Crates',
    singularName: 'Plastic Crate',
    pluralName: 'Plastic Crates',
    id: '58989d2c-b106-4a58-9fd1-071def3b2088',
    identifierPrefix: 'PCR',
    defaultTruckloadQuantity: 1000,
  },
  bulkbag: {
    slug: 'bulkbag',
    urlSlug: 'bulk-bags',
    name: 'Bulk Bags',
    singularName: 'Bulk Bag',
    pluralName: 'Bulk Bags',
    id: '6b345947-773c-4f87-ad31-0f4e27967693',
    identifierPrefix: 'BLK',
    defaultTruckloadQuantity: 7500,
  },
  cardboardbale: {
    slug: 'cardboardbale',
    urlSlug: 'cardboard-bales',
    name: 'Cardboard Bales',
    singularName: 'Cardboard Bale',
    pluralName: 'Cardboard Bales',
    id: '603f742c-1283-4fb0-a5c7-68237938d57d',
    identifierPrefix: 'CBL',
    defaultTruckloadQuantity: 38,
  },
  shippingbox: {
    slug: 'shippingbox',
    urlSlug: 'shipping-boxes',
    name: 'Shipping Boxes',
    singularName: 'Shipping Box',
    pluralName: 'Shipping Boxes',
    id: '6f2475c1-2447-4960-82a0-d0aca9dd9e75',
    identifierPrefix: 'SBX',
    defaultTruckloadQuantity: 100,
  },
  lumber: {
    slug: 'lumber',
    urlSlug: 'lumber',
    name: 'Lumber',
    singularName: 'Lumber',
    pluralName: 'Lumber',
    id: 'ebf75111-dda9-45ec-b1b1-09c9b3110342',
    identifierPrefix: 'LBR',
    defaultTruckloadQuantity: 1000,
  },
  equipment: {
    slug: 'equipment',
    urlSlug: 'equipment',
    name: 'Equipment',
    singularName: 'Equipment',
    pluralName: 'Equipment',
    id: '23cc4937-c911-4141-a5f1-428e48206b47',
    identifierPrefix: 'EQP',
    defaultTruckloadQuantity: 1,
  },
  movingbox: {
    slug: 'movingbox',
    urlSlug: 'moving-boxes',
    name: 'Moving Boxes',
    singularName: 'Moving Box',
    pluralName: 'Moving Boxes',
    id: '94da413e-11a6-45f6-b374-3433107dca23',
    identifierPrefix: 'MBX',
    defaultTruckloadQuantity: 30000,
  },
} as const

export const ALL_PRODUCTS: readonly ProductConfig[] = Object.values(PRODUCT_CATALOG)
